import { useEffect } from "react";
import { InboxPanel } from "../inbox/index.js";
import { useInboxDragStore } from "../../stores/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { MobileDrawer } from "./MobileDrawer.js";
import { useEdgeSwipe } from "./useEdgeSwipe.js";

/**
 * Mobile inbox: dragging a finger in from the RIGHT screen edge on a calendar
 * view slides the inbox panel out as an overlay drawer (the header's inbox
 * toggle is hidden on mobile).
 *
 * The scrim is PASSTHROUGH while open so the calendar grid behind stays live —
 * a long-pressed calendar block can be dragged onto the inbox panel to
 * un-schedule it (the touch equivalent of the desktop drag→inbox drop). Because
 * the scrim no longer catches a close tap, we close on a clean tap that lands
 * outside the panel (and not on a draggable block), swallowing the synthesized
 * mouse so it can't also fire the grid's tap-to-create.
 *
 * While an inbox item is itself being touch-dragged onto the calendar the drawer
 * goes fully transparent so the grid behind is the drop target.
 */
export function MobileInboxDrawer({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useI18n();
  useEdgeSwipe("right", () => onOpenChange(true), !open);
  const dragging = useInboxDragStore((s) => s.active);

  useEffect(() => {
    if (!open) return;
    let sx = 0, sy = 0, st = 0, candidate = false;
    const onDown = (e: TouchEvent) => {
      const t = e.touches[0];
      const el = e.target as HTMLElement | null;
      // Ignore touches on the panel itself or on a draggable block (the latter
      // starts a block→inbox drag) / the inbox drag ghost.
      if (!t || el?.closest?.("[data-inbox-drawer],[data-task-id],[data-quick-ghost]")) { candidate = false; return; }
      sx = t.clientX; sy = t.clientY; st = performance.now(); candidate = true;
    };
    const onUp = (e: TouchEvent) => {
      if (!candidate) return;
      candidate = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const moved = Math.abs(t.clientX - sx) > 10 || Math.abs(t.clientY - sy) > 10;
      if (!moved && performance.now() - st < 500) {
        e.preventDefault(); // swallow the synthetic mouse → no grid tap-to-create
        onOpenChange(false);
      }
    };
    window.addEventListener("touchstart", onDown, true);
    window.addEventListener("touchend", onUp, true);
    return () => {
      window.removeEventListener("touchstart", onDown, true);
      window.removeEventListener("touchend", onUp, true);
    };
  }, [open, onOpenChange]);

  return (
    <MobileDrawer
      side="right"
      open={open}
      onClose={() => onOpenChange(false)}
      label={t("common.inbox")}
      widthClassName="w-1/3 min-w-[136px]"
      transparent={dragging}
      scrimPassthrough
    >
      <InboxPanel onScheduledFromDrag={() => onOpenChange(false)} />
    </MobileDrawer>
  );
}
